import { Application } from '@tencent/alloy-renderer';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { SlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ThumbnailLogicService } from '@src/slide/content-viewer/service/canvas-renderer/entry/thumbnail/logic/thumbnail-logic-service';
import CanvasE2EManager from '@src/js/auto-test/canvas-e2e';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import { IEditorViewableServiceLayer } from '@src/slide/application/entry/toc/editor/base/service-layer/viewable-service-layer.interface';
import { IAttachmentManager } from '@src/slide/application/service/attachment-upload/attachment-manager.interface';
import { IPcEditorViewableServiceLayer } from '@src/slide/application/entry/toc/editor/pc/service-layer/viewable-service-layer.interface';
import { IEditorEditableServiceLayer } from '@src/slide/application/entry/toc/editor/base/service-layer/editable-service-layer.interface';
import { IInfrastructureBase } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import {
    IThumbnailRendererService,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/thumbnail/thumbnail-renderer-service.interface';

export class RenderLayerService implements IThumbnailRendererService {
    private application: Application<any>;
    private slideModel: ISlideModel;
    private slideComponent?: SlideComponent;
    private getPageScale: any;
    private region: string;
    private onDidRenderCallback: (() => void) | undefined;
    private uiDataService: IUiDataService;
    private selectionService: ISelectionService | undefined;
    private mediaManager: IMediaManager;
    private viewableServiceLayer: IEditorViewableServiceLayer | undefined;
    private editableServiceLayer: IEditorEditableServiceLayer | undefined;
    private attachmentManager: IAttachmentManager | undefined;
    private scale: number;
    private buildRenderTreeAsync: boolean;
    private infrastructureService: IInfrastructureBase;

    public constructor(
        slideModel: ISlideModel,
        canvasElement: HTMLCanvasElement,
        scale = 1,
        getPageScale: Function | null = null,
        region: string,
        enableEvent: boolean,
        buildRenderTreeAsync = true,
        infrastructureService: IInfrastructureBase,
        uiDataService: IUiDataService,
        mediaManager: IMediaManager,
        onDidRenderCallback?: () => void,
        selectionService?: ISelectionService,
        viewableServiceLayer?: IEditorViewableServiceLayer,
        editableServiceLayer?: IEditorEditableServiceLayer,
        createApplication?: () => Application,
    ) {
        this.slideModel = slideModel;
        this.getPageScale = getPageScale;
        this.region = region;
        this.buildRenderTreeAsync = buildRenderTreeAsync;
        this.onDidRenderCallback = onDidRenderCallback;
        this.infrastructureService = infrastructureService;
        this.uiDataService = uiDataService;
        this.selectionService = selectionService;
        this.mediaManager = mediaManager;
        this.viewableServiceLayer = viewableServiceLayer;
        this.editableServiceLayer = editableServiceLayer;
        this.attachmentManager = (viewableServiceLayer as IPcEditorViewableServiceLayer)?.attachmentManager;
        const resolution = Math.ceil(window.devicePixelRatio);

        // 异步构建启动时需要关闭autoRender，构建结束再打开
        const autoRender = !this.buildRenderTreeAsync;
        if (createApplication) {
            this.application = createApplication();
            this.application.resolution = resolution;
        } else {
            const target = new BrowserTarget();
            this.application = new Application(target, {
                interactive: enableEvent,
                native: canvasElement,
                delayDrawHit: false,
                notRenderOutsideView: true,
                autoRender,
                resolution,
            });
        }

        CanvasE2EManager.getInstance().addThumbApplication(slideModel, this.application);  // 测试环境存储application
        this.application.stage.forceRenderOutsideView = true;
        // 异步构建就只更新属性，不触犯render
        if (buildRenderTreeAsync) {
            this.updateInfo(scale);
        } else {
            this.setScale(scale);
        }

        this.init();
    }

    /**
     * 销毁app
     * @param clearView 是否保留canvas图像
     */
    public destroyLayer(clearView?: boolean): void {
        this.application?.dispose(clearView);
        CanvasE2EManager.getInstance().removeThumbApplication(this.slideModel);
        this.slideComponent = undefined;
    }

    public updateInfo(scale): void {
        this.scale = scale;
        const width = this.uiDataService.presentation.props.slideWidth * scale;
        const height = this.uiDataService.presentation.props.slideHeight * scale;
        this.application.width = width;
        this.application.height = height;
        this.application.stage.transform.scaleX = scale;
        this.application.stage.transform.scaleY = scale;
    }

    public setScale(scale: number): void {
        if (this.scale === scale) return;
        this.updateInfo(scale);
        this.slideComponent?.renderBackground();
        this.application.requestRender();
    }

    public getApplication = () => this.application;

    /**
     * 根据数据初始化渲染层
     * @private
     */
    private init() {
        const logicService = new ThumbnailLogicService();
        const slideComponent = new SlideComponent({
            slideModel: this.slideModel,
            getPageScale: this.getPageScale,
            getApplication: this.getApplication,
            region: this.region,
            fromScale: 1,
            logicService,
            asyncBuild: this.buildRenderTreeAsync,
            onDidRenderCallback: this.onDidRenderCallback,
            infrastructureService: this.infrastructureService,
            uiDataService: this.uiDataService,
            selectionService: this.selectionService,
            mediaManager: this.mediaManager,
            attachmentManager: this.attachmentManager,
            editingStateManager: this.viewableServiceLayer?.editingStateManager,
            addonManager: this.viewableServiceLayer?.addonManager,
            fontManager: this.viewableServiceLayer?.fontManager,
            // @ts-ignore
            linkManager: this.viewableServiceLayer?.linkManager,
            accessibilityManager: this.viewableServiceLayer?.accessibilityManager,
            undoRedoService: this.editableServiceLayer?.undoRedoService,
            // @ts-ignore
            masterLayoutEditService: this.editableServiceLayer?.masterLayoutEditService,
            gifService: this.viewableServiceLayer?.gifService,
        });
        this.slideComponent = slideComponent;
        this.application.stage.addChild(slideComponent);

        // 异步构建不触发render，等异步构建结束，由异步构建树启动，否则会带来多余的性能消耗
        if (!this.buildRenderTreeAsync) {
            this.application.requestRender();
        }
    }
}
